import _ from 'lodash';

import { DrawReviewDecision } from 'store/construction/types';

import { getDrawLabel } from '../components/DrawListSection/DrawListSection.utils';

// The API draw line item only carries budgetLineItemId + requested/percentComplete; the drawer's Line
// Items table needs the category / cost code / remaining budget, which live on the matching budget line.
// Join each draw line to its budget line by id so the table renders the cost code instead of "undefined".
export const enrichDrawLineItems = (lineItems = [], budgetLines = []) => {
  const budgetLineById = _.keyBy(budgetLines, 'id');

  return lineItems.map(lineItem => {
    const budgetLine = budgetLineById[lineItem.budgetLineItemId];
    // "Remaining" against THIS draw is the line budget still un-funded, EXCLUDING this draw's own
    // contribution. Once a draw is Approved/Disbursed its approved amount is already rolled into
    // budgetLine.fundedToDate, so a naive `budgeted - fundedToDate` double-counts THIS draw and
    // falsely flags an already-approved, within-budget line as over remaining (DEF-4). Add the draw
    // line's own approved amount back so the comparison is against the pre-this-draw remaining. Before
    // approval `approved` is null (contribution 0), so the pre-approval remaining is unchanged.
    const ownFundedContribution = lineItem.approved ?? 0;
    const budgetRemaining = _.isNil(budgetLine)
      ? null
      : (budgetLine.budgeted ?? 0) - (budgetLine.fundedToDate ?? 0) + ownFundedContribution;
    const isOverRemaining = !_.isNil(budgetRemaining) && lineItem.requested > budgetRemaining;

    return {
      ...lineItem,
      category: budgetLine?.description ?? lineItem.category ?? '—',
      costCode: budgetLine?.costCode ?? '—',
      budgetRemaining,
      isOverRemaining,
      // The over-draw banner reads overRemainingAmount; without it the amount rendered as "-" (DEF-4).
      overRemainingAmount: isOverRemaining ? lineItem.requested - budgetRemaining : null,
    };
  });
};

export const getClearedChecklistCount = (checklistItems = []) =>
  checklistItems.filter(item => item.isCleared).length;

export const getBlockingChecklistItem = (checklistItems = []) =>
  checklistItems.find(item => item.isBlocking && !item.isCleared) ?? null;

export const getDrawerTitle = drawRequest =>
  drawRequest
    ? `Draw Request ${getDrawLabel(drawRequest).replace(/^Draw\s*/i, '#')}`
    : 'Draw Request';

export const getDecisionConfirmation = ({ decision, drawRequest }) => {
  const label = getDrawLabel(drawRequest);

  if (decision === DrawReviewDecision.DENY) {
    return {
      message: `Deny ${label}?`,
      description:
        'The builder will be notified that this draw was denied. This does not disburse any funds.',
      onAcceptButtonName: 'Deny Draw',
    };
  }

  return {
    message: `Request changes on ${label}?`,
    description:
      'The builder will be asked to update and resubmit this draw request before it can be approved.',
    onAcceptButtonName: 'Request Changes',
  };
};
